(function () {
  const weatherSections = document.querySelectorAll('[data-cfsa-weather]');
  if (!weatherSections.length) return;

  const locationNameCache = new Map();
  const PORT_CANAVERAL = { latitude: 28.4089, longitude: -80.6034 };
  const SUN_ICON =
    '<svg class="cfsa-weather__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2M12 19.5v2M4.6 4.6 6 6M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4"/></svg>';

  const getPortCanaveralCoordinates = (container) => {
    const latitude = parseFloat(container.dataset.defaultLatitude || String(PORT_CANAVERAL.latitude));
    const longitude = parseFloat(container.dataset.defaultLongitude || String(PORT_CANAVERAL.longitude));
    return {
      latitude: Number.isFinite(latitude) ? latitude : PORT_CANAVERAL.latitude,
      longitude: Number.isFinite(longitude) ? longitude : PORT_CANAVERAL.longitude,
    };
  };

  const isNumericValue = (value) => value !== null && value !== '' && Number.isFinite(Number(value));

  // Open-Meteo labels miles per hour as "mp/h".
  const normalizeUnit = (unit) => (unit === 'mp/h' ? 'mph' : unit || '');

  // Ocean current velocity is only ever returned in km/h, so convert it to match the other speeds.
  const toMilesPerHour = (value, unit) => {
    if (isNumericValue(value) && (unit === 'km/h' || unit === 'kmh')) {
      return { value: Number(value) * 0.621371, unit: 'mph' };
    }
    return { value, unit: normalizeUnit(unit) };
  };

  const toFeet = (value, unit) => {
    const normalizedUnit = String(unit || '').trim().toLowerCase();
    if (isNumericValue(value) && ['m', 'meter', 'meters', 'metre', 'metres'].includes(normalizedUnit)) {
      return { value: Number(value) * 3.28084, unit: 'ft' };
    }
    return { value, unit: normalizedUnit === 'feet' || normalizedUnit === 'foot' ? 'ft' : unit };
  };

  const formatNumber = (value, options = {}) => {
    if (!isNumericValue(value)) return '';

    const number = Number(value);

    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 0,
      ...options,
    }).format(number);
  };

  const formatMeasurement = (value, unit = '', options = {}) => {
    if (!isNumericValue(value)) return 'Unavailable';
    const label = normalizeUnit(unit);
    return `${formatNumber(value, options)}${label ? ` ${label}` : ''}`;
  };

  const formatTime = (value) => {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const setText = (container, selector, value) => {
    const target = container.querySelector(selector);
    if (target) target.textContent = value;
  };

  const formatCoordinatePair = (latitude, longitude) =>
    `${formatNumber(latitude, { maximumFractionDigits: 4 })}, ${formatNumber(longitude, { maximumFractionDigits: 4 })}`;

  const formatPlaceName = (data) => {
    const address = data?.address || {};
    const locality =
      address.city ||
      address.town ||
      address.village ||
      address.hamlet ||
      address.municipality ||
      address.suburb ||
      address.county;
    const region = address.state || address.region;
    const countryCode = address.country_code ? String(address.country_code).toUpperCase() : '';

    const parts = [locality, region, countryCode].filter(Boolean);
    if (parts.length) return parts.join(', ');

    if (data?.display_name) {
      return String(data.display_name)
        .split(',')
        .slice(0, 3)
        .map((part) => part.trim())
        .filter(Boolean)
        .join(', ');
    }

    return '';
  };

  const resolveLocationTitle = async (container, latitude, longitude, preferredName = '') => {
    const targets = [
      {
        element: container.querySelector('[data-weather-location-heading]'),
        defaultText: container.dataset.defaultHeading,
      },
      {
        element: container.querySelector('[data-weather-location-title]'),
        defaultText: container.dataset.defaultLocationName,
      },
    ].filter((target) => target.element);

    if (!targets.length) return;

    const fallback = formatCoordinatePair(latitude, longitude);
    const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
    const { latitude: defaultLatitude, longitude: defaultLongitude } = getPortCanaveralCoordinates(container);
    const isDefaultLocation =
      Math.abs(latitude - defaultLatitude) < 0.0001 &&
      Math.abs(longitude - defaultLongitude) < 0.0001;

    const setLocationTargets = (value, useDefaultText = false) => {
      targets.forEach(({ element, defaultText }) => {
        element.textContent = useDefaultText && defaultText ? defaultText : value;
      });
    };

    if (preferredName) {
      locationNameCache.set(cacheKey, preferredName);
      setLocationTargets(preferredName);
      return;
    }

    if (isDefaultLocation) {
      const defaultName = container.dataset.defaultLocationName || container.dataset.defaultHeading || 'Port Canaveral';
      locationNameCache.set(cacheKey, defaultName);
      setLocationTargets(defaultName);
      return;
    }

    setLocationTargets(fallback);

    if (locationNameCache.has(cacheKey)) {
      setLocationTargets(locationNameCache.get(cacheKey) || fallback);
      return;
    }

    const params = new URLSearchParams({
      lat: String(latitude),
      lon: String(longitude),
      format: 'jsonv2',
      addressdetails: '1',
      'accept-language': 'en',
      zoom: '10',
    });

    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params.toString()}`);
      if (!response.ok) throw new Error(`Location lookup failed with ${response.status}`);

      const data = await response.json();
      const placeName = formatPlaceName(data) || fallback;
      locationNameCache.set(cacheKey, placeName);
      setLocationTargets(placeName);
    } catch (error) {
      locationNameCache.set(cacheKey, fallback);
      setLocationTargets(fallback);
      console.warn('[CFSA weather location]', error);
    }
  };

  const getCompassDirection = (value) => {
    if (!isNumericValue(value)) return '';

    const degrees = Number(value);
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return directions[Math.round(degrees / 45) % directions.length];
  };

  const formatWaveDirection = (value) =>
    isNumericValue(value) ? `${getCompassDirection(value)} · ${formatNumber(value)}°` : '';

  const formatCurrentDirection = (value) =>
    isNumericValue(value) ? `${getCompassDirection(value)} (${formatNumber(value)}°)` : '';

  const formatPeriod = (value) =>
    isNumericValue(value) ? `${formatNumber(value)} sec` : 'Unavailable';

  const formatTemperature = (value, unit = '') => {
    if (!isNumericValue(value)) return 'Unavailable';
    return `${formatNumber(value)}${unit || ''}`;
  };

  const getNearestHourlyIndex = (times, currentTime) => {
    if (!Array.isArray(times) || !times.length) return -1;

    const exactIndex = times.indexOf(currentTime);
    if (exactIndex >= 0) return exactIndex;

    const currentDate = new Date(currentTime);
    if (Number.isNaN(currentDate.getTime())) return 0;

    return times.reduce(
      (nearest, time, index) => {
        const date = new Date(time);
        if (Number.isNaN(date.getTime())) return nearest;

        const distance = Math.abs(date.getTime() - currentDate.getTime());
        return distance < nearest.distance ? { index, distance } : nearest;
      },
      { index: 0, distance: Infinity }
    ).index;
  };

  const renderForecast = (container, data, startIndex) => {
    const list = container.querySelector('[data-weather-hourly]');
    if (!list || !data.hourly || startIndex < 0) return;

    const forecastHours = Math.max(0, parseInt(container.dataset.forecastHours || '0', 10));
    if (!forecastHours) {
      list.hidden = true;
      return;
    }

    const times = data.hourly.time || [];
    const temperatures = data.hourly.temperature_2m || [];
    const winds = data.hourly.wind_speed_10m || [];
    const temperatureUnit = data.hourly_units?.temperature_2m || data.current_units?.temperature_2m || '';
    const windUnit = normalizeUnit(data.hourly_units?.wind_speed_10m || data.current_units?.wind_speed_10m || '');

    const items = times.slice(startIndex + 1, startIndex + 1 + forecastHours).map((time, offset) => {
      const index = startIndex + 1 + offset;
      const temperature = formatNumber(temperatures[index]);
      const wind = formatNumber(winds[index]);

      return `
        <li class="cfsa-weather__forecast-item">
          <span class="cfsa-weather__forecast-time">${formatTime(time)}</span>
          <span class="cfsa-weather__forecast-main">
            ${SUN_ICON}
            <strong class="cfsa-weather__forecast-temp">${temperature}${temperatureUnit ? ` ${temperatureUnit}` : ''}</strong>
          </span>
          <small class="cfsa-weather__forecast-wind">${wind}${windUnit ? ` ${windUnit}` : ''} wind</small>
        </li>
      `;
    });

    if (!items.length) {
      list.hidden = true;
      return;
    }

    list.innerHTML = items.join('');
    list.hidden = false;
  };

  const fetchMarine = async (container, latitude, longitude, fallbackTime) => {
    if (container.dataset.marineEnabled !== 'true') return;

    const status = container.querySelector('[data-marine-status]');
    const content = container.querySelector('[data-marine-content]');
    if (status) status.textContent = 'Loading marine conditions...';

    const params = new URLSearchParams({
      latitude: String(latitude),
      longitude: String(longitude),
      hourly:
        'wave_height,wave_direction,wave_period,swell_wave_height,swell_wave_period,sea_surface_temperature,ocean_current_velocity,ocean_current_direction',
      timezone: 'auto',
      forecast_days: '1',
    });

    if (container.dataset.marineLengthUnit) params.set('length_unit', container.dataset.marineLengthUnit);
    params.set('temperature_unit', 'fahrenheit');

    try {
      const response = await fetch(`https://marine-api.open-meteo.com/v1/marine?${params.toString()}`);
      if (!response.ok) throw new Error(`Marine request failed with ${response.status}`);

      const data = await response.json();
      const hourly = data.hourly || {};
      const units = data.hourly_units || {};
      const index = getNearestHourlyIndex(hourly.time || [], fallbackTime || new Date().toISOString());

      if (index < 0 || !Array.isArray(hourly.time)) throw new Error('Marine response had no hourly data');

      const waveHeight = hourly.wave_height?.[index];
      const wavePeriod = hourly.wave_period?.[index];
      const waveDirection = hourly.wave_direction?.[index];
      const swellHeight = hourly.swell_wave_height?.[index];
      const swellPeriod = hourly.swell_wave_period?.[index];
      const seaTemperature = hourly.sea_surface_temperature?.[index];
      const currentVelocity = hourly.ocean_current_velocity?.[index];
      const currentDirection = hourly.ocean_current_direction?.[index];

      const hasAnyMarineData = [
        waveHeight,
        wavePeriod,
        swellHeight,
        seaTemperature,
        currentVelocity,
      ].some(isNumericValue);

      if (!hasAnyMarineData) throw new Error('Marine response did not include usable values');

      const waveHeightFeet = toFeet(waveHeight, units.wave_height);
      const swellHeightFeet = toFeet(swellHeight, units.swell_wave_height);

      setText(
        container,
        '[data-marine-wave]',
        formatMeasurement(waveHeightFeet.value, waveHeightFeet.unit, { maximumFractionDigits: 1 })
      );
      setText(container, '[data-marine-period]', formatPeriod(wavePeriod));
      setText(
        container,
        '[data-marine-swell]',
        isNumericValue(swellHeight)
          ? `${formatMeasurement(swellHeightFeet.value, swellHeightFeet.unit, { maximumFractionDigits: 1 })}${isNumericValue(swellPeriod) ? ` @ ${formatPeriod(swellPeriod)}` : ''}`
          : 'Unavailable'
      );
      setText(container, '[data-marine-water-temp]', formatTemperature(seaTemperature, units.sea_surface_temperature));
      const currentSpeed = toMilesPerHour(currentVelocity, units.ocean_current_velocity);

      setText(
        container,
        '[data-marine-current]',
        isNumericValue(currentVelocity)
          ? `${formatMeasurement(currentSpeed.value, currentSpeed.unit, { maximumFractionDigits: 1 })}${isNumericValue(currentDirection) ? ` · ${formatCurrentDirection(currentDirection)}` : ''}`
          : 'Unavailable'
      );
      setText(container, '[data-marine-direction]', formatWaveDirection(waveDirection) || 'Unavailable');

      if (status) status.hidden = true;
      if (content) content.hidden = false;
    } catch (error) {
      if (status) status.textContent = 'Marine conditions are unavailable for this location.';
      if (content) content.hidden = true;
      console.warn('[CFSA marine weather]', error);
    }
  };

  const setCoordinateStatus = (container, message) => {
    setText(container, '[data-weather-coordinate-status]', message);
  };

  const setLoadingState = (container) => {
    const status = container.querySelector('[data-weather-status]');
    const content = container.querySelector('[data-weather-content]');
    const marineStatus = container.querySelector('[data-marine-status]');
    const marineContent = container.querySelector('[data-marine-content]');

    if (status) {
      status.hidden = false;
      status.textContent = 'Loading weather...';
    }
    if (content) content.hidden = true;
    if (marineStatus) {
      marineStatus.hidden = false;
      marineStatus.textContent = 'Loading marine conditions...';
    }
    if (marineContent) marineContent.hidden = true;
  };

  const isValidCoordinatePair = (latitude, longitude) =>
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  const syncActivePreset = (container, latitude, longitude) => {
    container.querySelectorAll('[data-weather-preset]').forEach((button) => {
      const presetLatitude = Number(button.dataset.latitude);
      const presetLongitude = Number(button.dataset.longitude);
      const isActive =
        Number.isFinite(presetLatitude) &&
        Number.isFinite(presetLongitude) &&
        Math.abs(latitude - presetLatitude) < 0.0001 &&
        Math.abs(longitude - presetLongitude) < 0.0001;

      button.setAttribute('aria-pressed', String(isActive));
    });
  };

  const loadWeather = async (container, latitude, longitude, locationName = '') => {
    const status = container.querySelector('[data-weather-status]');
    const content = container.querySelector('[data-weather-content]');
    const marineStatus = container.querySelector('[data-marine-status]');
    const marineContent = container.querySelector('[data-marine-content]');

    if (!isValidCoordinatePair(latitude, longitude)) {
      if (status) {
        status.hidden = false;
        status.textContent = 'Enter a valid latitude (-90 to 90) and longitude (-180 to 180), or use your browser location.';
      }
      if (content) content.hidden = true;
      if (marineStatus) {
        marineStatus.hidden = false;
        marineStatus.textContent = 'Enter a location to show marine conditions.';
      }
      if (marineContent) marineContent.hidden = true;
      setCoordinateStatus(container, '');
      return;
    }

    syncActivePreset(container, latitude, longitude);
    setLoadingState(container);
    resolveLocationTitle(container, latitude, longitude, locationName);

    const params = new URLSearchParams({
      latitude: String(latitude),
      longitude: String(longitude),
      current: 'temperature_2m,wind_speed_10m',
      hourly: 'temperature_2m,relative_humidity_2m,wind_speed_10m',
      timezone: 'auto',
      forecast_days: '1',
    });

    if (container.dataset.temperatureUnit) params.set('temperature_unit', container.dataset.temperatureUnit);
    params.set('wind_speed_unit', 'mph');

    try {
      const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
      if (!response.ok) throw new Error(`Weather request failed with ${response.status}`);

      const data = await response.json();
      const current = data.current || {};
      const currentUnits = data.current_units || {};
      const hourlyIndex = getNearestHourlyIndex(data.hourly?.time || [], current.time);
      const humidity = hourlyIndex >= 0 ? data.hourly?.relative_humidity_2m?.[hourlyIndex] : null;

      setText(container, '[data-weather-temp]', `${formatNumber(current.temperature_2m)} ${currentUnits.temperature_2m || ''}`.trim());
      setText(container, '[data-weather-wind]', `${formatNumber(current.wind_speed_10m)} ${normalizeUnit(currentUnits.wind_speed_10m)}`.trim());
      setText(container, '[data-weather-humidity]', Number.isFinite(Number(humidity)) ? `${formatNumber(humidity)}%` : 'Unavailable');
      setText(container, '[data-weather-updated]', current.time ? `Updated ${formatTime(current.time)}` : 'Updated just now');

      renderForecast(container, data, hourlyIndex);
      fetchMarine(container, latitude, longitude, current.time);
      const coordinatePair = formatCoordinatePair(latitude, longitude);
      setCoordinateStatus(
        container,
        locationName
          ? `Showing conditions for ${locationName} (${coordinatePair}).`
          : `Showing conditions for ${coordinatePair}.`
      );

      if (status) status.hidden = true;
      if (content) content.hidden = false;
    } catch (error) {
      if (status) status.textContent = 'Weather is temporarily unavailable. Please try again later.';
      console.warn('[CFSA weather]', error);
    }
  };

  const initWeatherSection = (container) => {
    if (container.dataset.weatherInitialized === 'true') return;
    container.dataset.weatherInitialized = 'true';

    const form = container.querySelector('[data-weather-location-form]');
    const latitudeInput = container.querySelector('[data-weather-latitude-input]');
    const longitudeInput = container.querySelector('[data-weather-longitude-input]');
    const locationButton = container.querySelector('[data-weather-use-location]');
    const presetButtons = container.querySelectorAll('[data-weather-preset]');

    const loadFromInputs = () => {
      const latitude = parseFloat(latitudeInput?.value || '');
      const longitude = parseFloat(longitudeInput?.value || '');
      loadWeather(container, latitude, longitude);
    };

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      loadFromInputs();
    });

    presetButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const latitude = Number(button.dataset.latitude);
        const longitude = Number(button.dataset.longitude);
        const locationName = button.dataset.weatherPresetName || button.textContent.trim();

        if (latitudeInput) latitudeInput.value = String(latitude);
        if (longitudeInput) longitudeInput.value = String(longitude);

        loadWeather(container, latitude, longitude, locationName);
      });
    });

    locationButton?.addEventListener('click', () => {
      if (!navigator.geolocation) {
        setCoordinateStatus(container, 'Browser location is not available. Enter latitude and longitude manually.');
        return;
      }

      const locationLabel = locationButton.querySelector('[data-weather-button-label]') || locationButton;
      const originalText = locationLabel.textContent;
      locationButton.disabled = true;
      locationLabel.textContent = 'Locating...';
      setCoordinateStatus(container, 'Requesting your location...');

      let settled = false;
      let timeoutId;
      const resetLocationButton = () => {
        window.clearTimeout(timeoutId);
        locationButton.disabled = false;
        locationLabel.textContent = originalText;
      };
      const finishLocationRequest = (callback) => {
        if (settled) return;
        settled = true;
        resetLocationButton();
        callback();
      };

      timeoutId = window.setTimeout(() => {
        finishLocationRequest(() => {
          setCoordinateStatus(container, 'Location request timed out. Try again or enter latitude and longitude manually.');
        });
      }, 18000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          finishLocationRequest(() => {
            const latitude = Number(position.coords.latitude.toFixed(5));
            const longitude = Number(position.coords.longitude.toFixed(5));

            if (latitudeInput) latitudeInput.value = String(latitude);
            if (longitudeInput) longitudeInput.value = String(longitude);

            loadWeather(container, latitude, longitude);
          });
        },
        () => {
          finishLocationRequest(() => {
            setCoordinateStatus(container, 'Location permission was denied or unavailable. Enter latitude and longitude manually.');
          });
        },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
      );
    });

    const portCanaveral = getPortCanaveralCoordinates(container);

    if (latitudeInput) latitudeInput.value = String(portCanaveral.latitude);
    if (longitudeInput) longitudeInput.value = String(portCanaveral.longitude);

    loadWeather(container, portCanaveral.latitude, portCanaveral.longitude, 'Port Canaveral');
  };

  weatherSections.forEach(initWeatherSection);
})();
